import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { MatIconModule } from '@angular/material/icon';
import { _AbcButtonComponent, _AbcCardComponent } from '@barthauer/abc/ui-components';
import { DxTooltipModule } from 'devextreme-angular';
import { SafeHtmlPipe } from '../../../../shared/pipes/safeHtml.pipe';
import { IRating } from '../../measure-detail/measurre-evaluation/measure-evaluation.component';
import { EURO_SVG, ICON_SVG } from '../../../../shared/assets/icons';
import { DataService } from '../../../../services/data.service';
import { MeasureService } from '../../measure.service';
import { Measures, MeasureStatusIcons } from '../measure-list.component';
import { RequestFormattedStatus } from '../../measure-detail/approval-request/enums';
import { ApprovalStatusConfig, ApprovalStatusConfigFactory } from '../../approval-status.config';
import { AppService } from '../../../../services/app.service';

@Component({
  selector: 'ubt-measure-card',
  standalone: true,
  imports: [CommonModule, MatIconModule, DxTooltipModule, SafeHtmlPipe, CurrencyPipe,  _AbcCardComponent,],
  templateUrl: './measure-card.component.html',
  styleUrls: ['./measure-card.component.scss'],
})
export class MeasureCardComponent {
  @Input() measures: Measures[] = [];
  @Input() noDataText: string = '';
  @Input() maxScore: number = 36;
  @Input() ratingCount: number = 6;
  @Output() cardClick = new EventEmitter<any>();
  @Output() favoriteToggle = new EventEmitter<{ event: Event; measure: any }>();
  @Output() contextMenuOpen = new EventEmitter<{ event: Event; measure: any }>();
  @Output() scoreTooltipShow = new EventEmitter<{ event: MouseEvent; measure: any }>();
  @Output() scoreTooltipHide = new EventEmitter<void>();
  private APPROVAL_STATUS_UI: Partial<Record<RequestFormattedStatus, ApprovalStatusConfig>>;

  public cardScoreTooltipTarget: HTMLElement | null = null;
  public isCardScoreTooltipVisible = false;
  public scoreTooltipData: IRating | null = null;

  public readonly iconSvg = ICON_SVG;
  public readonly euroSvg = EURO_SVG;

  constructor(
    public dataService: DataService,
    private appService: AppService,
    public measureService: MeasureService,
  ) {
    this.APPROVAL_STATUS_UI = ApprovalStatusConfigFactory.createConfig(this.dataService);
  }
  get locale(): string {
    return this.appService.locale;
  }
  public getApprovalConfig(status: RequestFormattedStatus): ApprovalStatusConfig | null {
    return this.APPROVAL_STATUS_UI[status] || null;
  }
  public getStatusClass(status: string): string {
    const statusMap = {
      [this.measureService.measureStatus[0].name]: 'done',
      [this.measureService.measureStatus[1].name]: 'in-progress',
    };
    return statusMap[status] || 'offen';
  }

  public getPersonName(personId: string): string {
    const person = this.dataService.cachedResponsiblesResponse?.find((p) => p.Id === personId);
    return person ? this.dataService.getResponsibleFullName(person) : 'N/A';
  }

  public onCardClick(measure: any): void {
    this.cardClick.emit(measure);
  }

  public onToggleFavorite(event: Event, measure: any): void {
    event.stopPropagation();
    this.favoriteToggle.emit({ event, measure });
  }

  public onContextMenuOpen(event: Event, measure: any): void {
    event.stopPropagation();
    this.contextMenuOpen.emit({ event, measure });
  }

  public toggleCollaborators(measure: any, event?: Event): void {
    if (event) {
      event.stopPropagation();
    }
    measure.showAllCollaborators = !measure.showAllCollaborators;
  }

  public showScoreTooltip(event: MouseEvent, measure: any): void {
    if (measure.TotalScore) {
      this.cardScoreTooltipTarget = event.currentTarget as HTMLElement;
      this.isCardScoreTooltipVisible = true;
      this.scoreTooltipData = {
        measureId: measure.Id,
        TotalScore: measure.TotalScore,
        Effort: measure.Effort,
        Benefit: measure.Benefit,
        Impact: measure.Impact,
        Cost: measure.Cost,
      };
      this.scoreTooltipShow.emit({ event, measure });
    }
  }

  public hideScoreTooltip(): void {
    this.isCardScoreTooltipVisible = false;
    this.cardScoreTooltipTarget = null;
    this.scoreTooltipHide.emit();
  }
}
